/** Progressive, local component state. No dependency or animation runtime. */
class FCRoutine extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.tabs = [...this.querySelectorAll('[data-routine-choice]')];
    this.panels = [...this.querySelectorAll('[data-routine-panel]')];
    if (!this.tabs.length || this.tabs.length !== this.panels.length) return;
    this.classList.add('fc-routine--enhanced');
    const list = this.querySelector('[data-routine-choices]');
    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-orientation', 'vertical');
    list.setAttribute('aria-labelledby', this.querySelector('h2').id);
    for (const [i, tab] of this.tabs.entries()) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', this.panels[i].id);
      this.panels[i].setAttribute('role', 'tabpanel');
      this.panels[i].setAttribute('aria-labelledby', tab.id);
      this.panels[i].tabIndex = 0;
      tab.addEventListener('click', (event) => { event.preventDefault(); this.select(i); }, {signal:this.controller.signal});
      if(matchMedia('(hover: hover) and (pointer: fine)').matches) tab.addEventListener('pointerenter',()=>this.select(i),{signal:this.controller.signal});
      tab.addEventListener('focus',()=>this.select(i),{signal:this.controller.signal});
      tab.addEventListener('keydown', (event) => {
        let next;
        if (['ArrowDown','ArrowRight'].includes(event.key)) next=(i+1)%this.tabs.length;
        if (['ArrowUp','ArrowLeft'].includes(event.key)) next=(i-1+this.tabs.length)%this.tabs.length;
        if(event.key==='Home') next=0;
        if(event.key==='End') next=this.tabs.length-1;
        if(event.key===' ') next=i;
        if(next===undefined) return;
        event.preventDefault();this.select(next);this.tabs[next].focus();
      }, {signal:this.controller.signal});
    }
    this.select(0);
    document.addEventListener('shopify:block:select', event => {
      const index=this.tabs.findIndex(tab=>tab.contains(event.target));
      if(index>=0) this.select(index);
    }, {signal:this.controller.signal});
  }
  select(index) {
    for(const [i,tab] of this.tabs.entries()) {
      tab.setAttribute('aria-selected',String(i===index));tab.tabIndex=i===index?0:-1;
      this.panels[i].hidden=i!==index;
    }
  }
  disconnectedCallback(){this.controller?.abort();}
}
class FCGallery extends HTMLElement {
  connectedCallback(){
    this.controller?.abort();this.controller=new AbortController();
    this.items=[...this.querySelectorAll('[data-gallery-item]')];
    this.buttons=[...this.querySelectorAll('[data-gallery-select]')];
    if(!this.items.length||this.items.length!==this.buttons.length)return;
    this.classList.add('fc-gallery--enhanced');
    for(const [i,button] of this.buttons.entries()) button.addEventListener('click',()=>this.select(i),{signal:this.controller.signal});
    this.select(0);
  }
  select(index){
    for(const [i,item] of this.items.entries()) {
      item.hidden=i!==index;this.buttons[i].setAttribute('aria-current',String(i===index));
      if(i!==index) for(const video of item.querySelectorAll('video'))video.pause();
      // Remove hidden external player source so audio cannot continue in a hidden panel.
      for(const frame of item.querySelectorAll('iframe')) {
        if(i!==index&&frame.getAttribute('src')){frame.dataset.playerSrc=frame.getAttribute('src');frame.removeAttribute('src');}
        else if(i===index&&frame.dataset.playerSrc&&!frame.getAttribute('src'))frame.setAttribute('src',frame.dataset.playerSrc);
      }
    }
  }
  disconnectedCallback(){this.controller?.abort();}
}
if(!customElements.get('fc-routine'))customElements.define('fc-routine',FCRoutine);
if(!customElements.get('fc-gallery'))customElements.define('fc-gallery',FCGallery);

class FCStoryCarousel extends HTMLElement{
  connectedCallback(){
    this.controller?.abort();this.controller=new AbortController();
    this.tabs=[...this.querySelectorAll('[data-story-choice]')];this.panels=[...this.querySelectorAll('[data-story-panel]')];this.toggle=this.querySelector('[data-story-toggle]');this.status=this.querySelector('[data-story-status]');
    if(!this.tabs.length||this.tabs.length!==this.panels.length)return;
    this.classList.add('fc-story--enhanced');
    this.querySelector('[data-story-controls]')?.setAttribute('role','tablist');
    this.tabs.forEach((tab,i)=>{
      tab.setAttribute('role','tab');tab.setAttribute('aria-controls',this.panels[i].id);
      this.panels[i].setAttribute('role','tabpanel');this.panels[i].setAttribute('aria-labelledby',tab.id);
      tab.addEventListener('click',()=>{this.select(i);this.pause();},{signal:this.controller.signal});
      tab.addEventListener('focus',()=>{this.select(i);this.pause();},{signal:this.controller.signal});
      if(matchMedia('(hover:hover) and (pointer:fine)').matches)tab.addEventListener('pointerenter',()=>{this.select(i);this.pause();},{signal:this.controller.signal});
      tab.addEventListener('keydown',event=>{if(!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key))return;event.preventDefault();const step=['ArrowRight','ArrowDown'].includes(event.key)?1:-1;const next=(i+step+this.tabs.length)%this.tabs.length;this.select(next);this.tabs[next].focus();},{signal:this.controller.signal});
    });
    this.addEventListener('pointerenter',()=>this.stopTimer(),{signal:this.controller.signal});
    this.addEventListener('pointerleave',()=>this.startTimer(),{signal:this.controller.signal});
    this.addEventListener('focusin',event=>{if(event.target!==this.toggle)this.pause();},{signal:this.controller.signal});
    this.querySelectorAll('video').forEach(video=>video.addEventListener('play',()=>this.pause(),{signal:this.controller.signal}));
    this.toggle?.addEventListener('click',()=>{this.userPaused=!this.userPaused;this.updateToggle();if(this.userPaused)this.stopTimer();else this.startTimer();},{signal:this.controller.signal});
    this.updateToggle();
    this.select(0);this.startTimer();
  }
  select(index){this.index=index;this.tabs.forEach((tab,i)=>{const active=i===index;tab.setAttribute('aria-selected',String(active));tab.tabIndex=active?0:-1;this.panels[i].hidden=!active;if(!active)this.panels[i].querySelector('video')?.pause();});if(this.status){const title=this.tabs[index]?.querySelector('strong')?.textContent?.trim();this.status.textContent=title?`Showing scene ${index+1} of ${this.tabs.length}: ${title}`:'';}}
  startTimer(){if(this.dataset.autoplay!=='true'||matchMedia('(prefers-reduced-motion: reduce)').matches||this.userPaused){this.classList.add('fc-story--paused');return;}clearInterval(this.timer);this.classList.remove('fc-story--paused');this.timer=setInterval(()=>this.select((this.index+1)%this.tabs.length),5000);}
  stopTimer(){clearInterval(this.timer);this.classList.add('fc-story--paused');}
  pause(){this.userPaused=true;this.stopTimer();this.updateToggle();}
  updateToggle(){if(!this.toggle)return;const paused=Boolean(this.userPaused);this.toggle.setAttribute('aria-pressed',String(paused));this.toggle.setAttribute('aria-label',paused?'Play story autoplay':'Pause story autoplay');const label=this.toggle.querySelector('[data-story-toggle-label]');if(label)label.textContent=paused?'Play':'Pause';const icon=this.toggle.querySelector('[aria-hidden]');if(icon)icon.textContent=paused?'▶':'Ⅱ';}
  disconnectedCallback(){this.stopTimer();this.controller?.abort();}
}
if(!customElements.get('fc-story-carousel'))customElements.define('fc-story-carousel',FCStoryCarousel);

class FCFaqRichtext extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enhanced === 'true') return;
    const source = [...this.children];
    const groups = [];
    let current;
    for (const node of source) {
      if (node.tagName === 'H2') {
        current = { heading: node, content: [] };
        groups.push(current);
      } else if (current) current.content.push(node);
    }
    if (!groups.length) return;
    const fragment = document.createDocumentFragment();
    groups.forEach(({ heading, content }) => {
      const detail = document.createElement('details');
      const summary = document.createElement('summary');
      const answer = document.createElement('div');
      summary.textContent = heading.textContent;
      content.forEach(node => answer.append(node));
      detail.append(summary, answer);
      fragment.append(detail);
    });
    this.replaceChildren(fragment);
    this.dataset.enhanced = 'true';
  }
}
if(!customElements.get('fc-faq-richtext'))customElements.define('fc-faq-richtext',FCFaqRichtext);

document.querySelectorAll('.fc-overview__header,.fc-overview__metrics,.fc-story-panel,.fc-product-section__heading,.fc-details-layout,.fc-product-faq__aside,.fc-product-faq__item,.fc-resource-card,.fc-review-panel,.fc-related__empty').forEach(element=>element.setAttribute('data-fc-reveal',''));
const revealItems=[...document.querySelectorAll('[data-fc-reveal]')];
if('IntersectionObserver' in window&&!matchMedia('(prefers-reduced-motion: reduce)').matches){
  document.documentElement.classList.add('fc-motion-ready');
  const revealObserver=new IntersectionObserver(entries=>{
    for(const entry of entries)if(entry.isIntersecting){entry.target.classList.add('fc-is-visible');revealObserver.unobserve(entry.target);}
  },{rootMargin:'0px 0px -8% 0px',threshold:.08});
  revealItems.forEach(item=>revealObserver.observe(item));
}else revealItems.forEach(item=>item.classList.add('fc-is-visible'));

class FCProductMedia extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    this.track = this.querySelector('[data-media-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-item]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-select]')];
    this.counter = this.querySelector('[data-gallery-counter]');
    this.status = this.querySelector('[data-gallery-status]');
    this.previousButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.dialogImage = this.querySelector('[data-gallery-dialog-image]');
    this.dialogLabel = this.querySelector('[data-gallery-dialog-label]');
    this.dialogTrigger = null;
    this.index = 0;
    this.querySelectorAll('[data-gallery-zoom]').forEach(button => button.addEventListener('click', () => this.openDialog(button), {signal:this.controller.signal}));
    this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.dialog?.close(), {signal:this.controller.signal});
    this.dialog?.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, {signal:this.controller.signal});
    this.dialog?.addEventListener('close', () => requestAnimationFrame(() => this.dialogTrigger?.focus()), {signal:this.controller.signal});
    this.closest('#purchase')?.addEventListener('fc:variant-media', event => {
      const index = this.slides.findIndex(slide => slide.dataset.mediaId === event.detail.mediaId);
      if (index >= 0) this.select(index);
    }, {signal:this.controller.signal});
    if (!this.track || this.slides.length < 2) return;
    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.select(index), {signal:this.controller.signal}));
    this.previousButton?.addEventListener('click', () => this.select(this.index - 1), {signal:this.controller.signal});
    this.nextButton?.addEventListener('click', () => this.select(this.index + 1), {signal:this.controller.signal});
    this.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      if (event.target !== this && !event.target.matches?.('[data-gallery-select],[data-gallery-prev],[data-gallery-next]')) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
      this.select(next); this.thumbs[this.index]?.focus();
    }, {signal:this.controller.signal});
    let startX = 0;
    let activePointerId = null;
    this.track.addEventListener('pointerdown', event => {
      if (event.target.closest('button,video,iframe,model-viewer')) return;
      event.preventDefault();
      startX = event.clientX;
      activePointerId = event.pointerId;
      this.track.setPointerCapture?.(event.pointerId);
      this.classList.add('is-dragging');
    }, {signal:this.controller.signal});
    this.track.addEventListener('pointercancel', event => {
      if (event.pointerId !== activePointerId) return;
      activePointerId = null;
      this.classList.remove('is-dragging');
    }, {signal:this.controller.signal});
    this.track.addEventListener('pointerup', event => {
      if (event.pointerId !== activePointerId) return;
      const distance = event.clientX - startX;
      if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      activePointerId = null;
      this.classList.remove('is-dragging');
      if (Math.abs(distance) > 42) this.select(this.index + (distance < 0 ? 1 : -1));
    }, {signal:this.controller.signal});
    this.resizeObserver = new ResizeObserver(() => this.position(false));
    this.resizeObserver.observe(this);
    this.select(0, false);
  }
  position(animate = true) {
    const slide = this.slides[this.index]; if (!slide) return;
    const viewportWidth = this.track.parentElement?.clientWidth || this.clientWidth;
    const centered = this.index === 0 ? 0 : slide.offsetLeft - ((viewportWidth - slide.offsetWidth) / 2);
    const maximum = Math.max(0, this.track.scrollWidth - viewportWidth);
    this.style.setProperty('--media-offset', `${-Math.max(0, Math.min(centered, maximum))}px`);
    this.classList.toggle('is-instant', !animate);
    if (!animate) requestAnimationFrame(() => this.classList.remove('is-instant'));
  }
  select(index, animate = true) {
    this.index = Math.max(0, Math.min(index, this.slides.length - 1));
    this.position(animate);
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.toggleAttribute('data-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelectorAll('button,video,iframe,model-viewer').forEach(control => {
        if (active) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', '-1');
      });
    });
    this.thumbs.forEach((thumb, i) => {
      const active = i === this.index;
      if (active) thumb.setAttribute('aria-current','true');
      else thumb.removeAttribute('aria-current');
      thumb.tabIndex = active ? 0 : -1;
    });
    if (this.counter) this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
    if (this.status) this.status.textContent = `Showing media ${this.index + 1} of ${this.slides.length}`;
    if (this.previousButton) this.previousButton.disabled = this.index === 0;
    if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
    if (this.previousButton) this.previousButton.setAttribute('aria-controls', this.slides[this.index]?.id || '');
    if (this.nextButton) this.nextButton.setAttribute('aria-controls', this.slides[this.index]?.id || '');
    this.pauseInactiveMedia();
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.thumbs[this.index]?.scrollIntoView({behavior: animate && !reduceMotion ? 'smooth' : 'auto', block:'nearest', inline:'nearest'});
  }
  pauseInactiveMedia() {
    this.slides.forEach((slide, index) => {
      if (index === this.index) return;
      slide.querySelectorAll('video').forEach(video => video.pause());
      slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
      slide.querySelectorAll('iframe').forEach(frame => {
        frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:''}), '*');
        frame.contentWindow?.postMessage({method:'pause'}, '*');
      });
    });
  }
  openDialog(button) {
    if (!this.dialog || !this.dialogImage || !button.dataset.fullSrc) return;
    this.dialogImage.src = button.dataset.fullSrc;
    this.dialogImage.alt = button.dataset.fullAlt || '';
    const imageIndex = this.slides.indexOf(button.closest('[data-gallery-item]'));
    if (this.dialogLabel) this.dialogLabel.textContent = `${button.dataset.fullAlt || 'Product image'} · ${imageIndex >= 0 ? imageIndex + 1 : this.index + 1} of ${this.slides.length}`;
    this.dialogTrigger = button;
    this.dialog.showModal();
  }
  disconnectedCallback(){ this.controller?.abort(); this.resizeObserver?.disconnect(); }
}
if(!customElements.get('fc-product-media')) customElements.define('fc-product-media', FCProductMedia);

const productSectionLinks = [...document.querySelectorAll('.fc-product-nav a[href^="#"]')];
if (productSectionLinks.length) {
  const productNavScroller = document.querySelector('.fc-product-nav__inner');
  const productSections = productSectionLinks.map(link => document.querySelector(link.hash)).filter(Boolean);
  let lockedSectionId = '';
  const revealCurrentLink = link => {
    if (!productNavScroller || productNavScroller.scrollWidth <= productNavScroller.clientWidth) return;
    const scrollerBox = productNavScroller.getBoundingClientRect();
    const linkBox = link.getBoundingClientRect();
    const target = productNavScroller.scrollLeft + linkBox.left - scrollerBox.left - ((scrollerBox.width - linkBox.width) / 2);
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    productNavScroller.scrollTo({left: Math.max(0, target), behavior: reduceMotion ? 'auto' : 'smooth'});
  };
  const setCurrentSection = id => productSectionLinks.forEach(link => {
    const current = link.hash === `#${id}`;
    link.classList.toggle('is-active', current);
    if (current) {
      link.setAttribute('aria-current', 'location');
      revealCurrentLink(link);
    }
    else link.removeAttribute('aria-current');
  });
  const updateFromScroll = () => {
    if (lockedSectionId) return;
    const marker = window.scrollY + Math.max(120, window.innerHeight * .24);
    let current = productSections[0];
    productSections.forEach(section => {
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      if (sectionTop <= marker) current = section;
    });
    if (current) setCurrentSection(current.id);
  };
  let frame;
  window.addEventListener('scroll', () => {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = null; updateFromScroll(); });
  }, {passive:true});
  productSectionLinks.forEach(link => link.addEventListener('click', () => {
    lockedSectionId = link.hash.slice(1);
    setCurrentSection(lockedSectionId);
  }));
  const releaseSectionLock = () => {
    if (!lockedSectionId) return;
    lockedSectionId = '';
    updateFromScroll();
  };
  window.addEventListener('wheel', releaseSectionLock, {passive:true});
  window.addEventListener('touchmove', releaseSectionLock, {passive:true});
  window.addEventListener('pointerdown', releaseSectionLock, {passive:true});
  window.addEventListener('keydown', event => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) releaseSectionLock();
  });
  updateFromScroll();
}

document.querySelectorAll('[data-quantity-minus],[data-quantity-plus]').forEach(button => button.addEventListener('click', () => {
  const input = button.parentElement.querySelector('input[type="number"]'); if (!input) return;
  const step = button.hasAttribute('data-quantity-plus') ? 1 : -1;
  input.value = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + step);
  input.dispatchEvent(new Event('change', {bubbles:true}));
}));

// Keep native details navigation predictable on desktop and mobile: one menu
// stays open at a time, outside clicks close it, and Escape returns focus to
// the trigger. This preserves the no-JS details fallback while improving the
// keyboard path for the enhanced theme.
const navigationDetails = [...document.querySelectorAll('.fc-nav-group, .fc-header__menu')];
navigationDetails.forEach(detail => {
  detail.addEventListener('toggle', () => {
    if (!detail.open) return;
    navigationDetails.forEach(other => {
      const sharesHierarchy = other.contains(detail) || detail.contains(other);
      if (other !== detail && !sharesHierarchy) other.removeAttribute('open');
    });
  });
});
document.addEventListener('click', event => {
  if (navigationDetails.some(detail => detail.contains(event.target))) return;
  navigationDetails.forEach(detail => detail.removeAttribute('open'));
});
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  const openDetail = navigationDetails.filter(detail => detail.open).at(-1);
  if (!openDetail) return;
  openDetail.removeAttribute('open');
  openDetail.querySelector('summary')?.focus();
});

// Return keyboard and assistive-technology users to contact-form feedback
// after Shopify renders the submitted page.
document.querySelector('[data-contact-result]')?.focus({preventScroll:false});
